import { useMemo } from 'react';

import { DocumentType, ResourceType } from '../core/types';
import { resourceTypeToLegacy } from '../utils/resource-type.utils';

const buildQueryString = (queryParams?: Record<string, unknown>): string => {
	if (!queryParams || Object.keys(queryParams).length === 0) return '';
	const _queryString = Object.entries(queryParams)
		.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value as string)}`)
		.join('&');
	return `?${_queryString}`;
};

export interface ResourceEndpoints {
	/**
	 * The legacy collection name for this resource type
	 * (e.g., 'orders' for CHECKOUTS, 'items' for ITEMS)
	 */
	collection: string;

	/**
	 * Get full URL for a specific resource
	 * @param id - The resource ID (empty string for collection-level endpoints)
	 * @param path - Optional path to append (e.g., '/update', '/call/method')
	 * @param queryParams - Optional query parameters
	 * @returns Full URL (e.g., 'items/123' or 'items/123/call/method')
	 */
	getUrl: (id: string, path?: string, queryParams?: Record<string, unknown>) => string;

	/**
	 * Get full URL for creating a resource
	 * @returns Full URL (e.g., 'items/create')
	 */
	getCreateUrl: () => string;

	/**
	 * Get full URL for searching resources
	 * @returns Full URL (e.g., 'items/search')
	 */
	getSearchUrl: () => string;

	/**
	 * Get full URL for calling a method on a resource
	 * @param id - The resource ID (empty string for collection-level calls)
	 * @param method - The method name
	 * @param useCallPrefix - Whether to use '/call' prefix (default: true)
	 * @param queryParams - Optional query parameters
	 * @returns Full URL (e.g., 'items/123/call/setStatus')
	 */
	getCallUrl: (id: string, method: string, useCallPrefix?: boolean, queryParams?: Record<string, unknown>) => string;

	/**
	 * Get full URL for deleting a resource
	 * @param id - The resource ID
	 * @returns Full URL (e.g., 'items/123/delete')
	 */
	getDeleteUrl: (id?: string) => string;

	/**
	 * Get full URL for updating a resource
	 * @param id - The resource ID
	 * @returns Full URL (e.g., 'items/123/update')
	 */
	getUpdateUrl: (id: string) => string;
}

/**
 * Hook for generating resource endpoints based on resource type
 *
 * This hook provides resource-relative paths for the v3 API client.
 * All methods return resource-relative paths for the v3 API client.
 *
 * @param resourceType - The resource type (ResourceType enum or DocumentType)
 * @returns Object with utility functions for generating full URLs
 *
 * @example
 * ```tsx
 * const endpoints = useResourceEndpoints(ResourceType.ITEMS);
 *
 * // Convenience methods for common operations
 * const createUrl = endpoints.getCreateUrl();
 * // Returns: 'items/create'
 *
 * const callUrl = endpoints.getCallUrl('123', 'setStatus');
 * // Returns: 'items/123/call/setStatus'
 *
 * const deleteUrl = endpoints.getDeleteUrl('123');
 * // Returns: 'items/123/delete'
 *
 * // For custom paths
 * const customUrl = endpoints.getUrl('123', '/custom/path', { param: 'value' });
 * // Returns: 'items/123/custom/path?param=value'
 * ```
 */
export const useResourceEndpoints = (resourceType: ResourceType | DocumentType): ResourceEndpoints => {
	const collection = useMemo(() => resourceTypeToLegacy(resourceType), [resourceType]);

	const getUrl = useMemo(
		() =>
			(id: string, path = '', queryParams?: Record<string, unknown>): string => {
				// Validate id doesn't contain path traversal or dangerous characters
				if (id && (id.includes('..') || id.includes('//') || id.includes('\\'))) {
					throw new Error('Invalid resource ID: contains path traversal characters');
				}

				// Validate id length to prevent extremely long IDs
				if (id && id.length > 1000) {
					throw new Error('Invalid resource ID: exceeds maximum length of 1000 characters');
				}

				// Validate path doesn't contain path traversal
				if (path && (path.includes('..') || path.includes('//') || path.includes('\\'))) {
					throw new Error('Invalid path: contains path traversal characters');
				}

				// Ensure path starts with / if provided
				if (path && !path.startsWith('/')) {
					throw new Error('Invalid path: must start with /');
				}

				const queryString = buildQueryString(queryParams);

				let endpoint: string;

				if (!id) {
					endpoint = collection;
				} else if (path) {
					endpoint = `${collection}/${id}${path}`;
				} else {
					endpoint = `${collection}/${id}`;
				}

				return `${endpoint}${queryString}`;
			},
		[collection]
	);

	const getCreateUrl = useMemo(
		() => (): string => {
			return `${collection}/create`;
		},
		[collection]
	);

	const getSearchUrl = useMemo(
		() => (): string => {
			return `${collection}/search`;
		},
		[collection]
	);

	const getCallUrl = useMemo(
		() =>
			(id: string, method: string, useCallPrefix = true, queryParams?: Record<string, unknown>): string => {
				const callPath = useCallPrefix ? `/call/${method}` : `/${method}`;
				let endpoint: string;

				if (id) {
					endpoint = `${collection}/${id}${callPath}`;
				} else {
					endpoint = `${collection}${callPath}`;
				}

				return `${endpoint}${buildQueryString(queryParams)}`;
			},
		[collection]
	);

	const getDeleteUrl = useMemo(
		() =>
			(id?: string): string => {
				return `${collection}${id ? `/${id}` : ''}/delete`;
			},
		[collection]
	);

	const getUpdateUrl = useMemo(
		() =>
			(id: string): string => {
				return `${collection}/${id}/update`;
			},
		[collection]
	);

	return {
		collection,
		getUrl,
		getCreateUrl,
		getSearchUrl,
		getCallUrl,
		getDeleteUrl,
		getUpdateUrl,
	};
};
